import React, { useState, useEffect, useCallback, useRef } from 'react';
import PostForm from './components/PostForm';
import PostList from './components/PostList';
import PostEditModal from './components/PostEditModal';
import Header from './components/Header';
import Footer from './components/Footer';
import AuthModal from './components/AuthModal';
import PopupManager from './components/PopupManager';
import { verifyAuth } from './services/api';

function App() {
  const [activeTab, setActiveTab] = useState('create');
  const [selectedPost, setSelectedPost] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  
  // Auto-logout system state
  const sessionStartTime = useRef(null);
  const [showLogoutWarning, setShowLogoutWarning] = useState(false);
  const [logoutCountdown, setLogoutCountdown] = useState(0);
  const [logoutTimer, setLogoutTimer] = useState(null);
  const [warningTimer, setWarningTimer] = useState(null);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(0);
  
  // Auto-logout configuration
  const AUTO_LOGOUT_TIME = 30 * 60 * 1000; // 30 minutes in milliseconds
  const WARNING_TIME = 5 * 60 * 1000; // 5 minutes warning

  // Handle logout function - defined early to avoid circular dependency
  const handleLogout = useCallback(() => {
    // Clear timers
    if (logoutTimer) {
      clearTimeout(logoutTimer);
      setLogoutTimer(null);
    }
    if (warningTimer) {
      clearInterval(warningTimer);
      setWarningTimer(null);
    }
    
    // Clear stored authentication
    localStorage.removeItem('wp_auth');
    localStorage.removeItem('wp_username');
    localStorage.removeItem('wp_token');
    localStorage.removeItem('wp_nonce');
    localStorage.removeItem('wp_session_user');
    localStorage.removeItem('wp_user');
    
    // Reset state
    setIsAuthenticated(false);
    setUser(null);
    setShowAuthModal(true);
    setShowLogoutWarning(false);
    setLogoutCountdown(0);
  }, []);

  // Check authentication on app load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authResult = await verifyAuth();
        
        setIsAuthenticated(authResult.isAuthenticated);
        setUser(authResult.user);
        
        // If authenticated, initialize session start time
        if (authResult.isAuthenticated) {
          sessionStartTime.current = Date.now();
        } else {
          setShowAuthModal(true);
        }
      } catch (error) {
        setIsAuthenticated(false);
        setUser(null);
        setShowAuthModal(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Simple session timer - no activity tracking
  useEffect(() => {
    if (!isAuthenticated || sessionStartTime.current === null) return;

    // Clear any existing timers
    if (logoutTimer) {
      clearTimeout(logoutTimer);
      setLogoutTimer(null);
    }
    if (warningTimer) {
      clearInterval(warningTimer);
      setWarningTimer(null);
    }

    // Set warning timer (5 minutes before logout)
    const warningTimerId = setTimeout(() => {
      setShowLogoutWarning(true);
      setLogoutCountdown(Math.ceil(WARNING_TIME / 1000));
      
      // Start countdown
      const countdownInterval = setInterval(() => {
        setLogoutCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            handleLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      setWarningTimer(countdownInterval);
    }, AUTO_LOGOUT_TIME - WARNING_TIME);
    
    // Set logout timer
    const logoutTimerId = setTimeout(() => {
      handleLogout();
    }, AUTO_LOGOUT_TIME);
    setLogoutTimer(logoutTimerId);

    // Cleanup
    return () => {
      if (logoutTimerId) clearTimeout(logoutTimerId);
      if (warningTimerId) clearTimeout(warningTimerId);
    };
  }, [isAuthenticated, sessionStartTime.current]);


  // Update session time left every second
  useEffect(() => {
    if (!isAuthenticated || sessionStartTime.current === null) {
      setSessionTimeLeft(0);
      return;
    }

    const updateSessionTime = () => {
      const timeSinceStart = Date.now() - sessionStartTime.current;
      const timeLeft = Math.max(0, AUTO_LOGOUT_TIME - timeSinceStart);
      setSessionTimeLeft(timeLeft);
    };

    updateSessionTime(); // Update immediately
    const interval = setInterval(updateSessionTime, 1000); // Update every second

    return () => clearInterval(interval);
  }, [isAuthenticated, AUTO_LOGOUT_TIME, sessionStartTime.current]);

  const handleAuthSuccess = (result) => {
    setIsAuthenticated(true);
    setUser(result.user);
    setShowAuthModal(false);
    // Initialize session start time only on successful authentication
    sessionStartTime.current = Date.now();
  };

  // Extend session (reset timers)
  const extendSession = useCallback(() => {
    sessionStartTime.current = Date.now();
    setShowLogoutWarning(false);
    setLogoutCountdown(0);
    
    // Clear existing timers
    if (logoutTimer) {
      clearTimeout(logoutTimer);
      setLogoutTimer(null);
    }
    if (warningTimer) {
      clearInterval(warningTimer);
      setWarningTimer(null);
    }
  }, []);

  const handleEditPost = (post) => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    setSelectedPost(post);
    setEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    setEditModalOpen(false);
    setSelectedPost(null);
    // Trigger refresh of post list
    setRefreshTrigger(prev => prev + 1);
  };

  const handleCloseModal = () => {
    setEditModalOpen(false);
    setSelectedPost(null);
  };

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header 
        user={user} 
        onLogout={handleLogout} 
        sessionTimeLeft={sessionTimeLeft}
        onExtendSession={extendSession}
        isAuthenticated={isAuthenticated}
      />
      
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {/* Only show main content if authenticated */}
        {isAuthenticated ? (
          <>
            {/* Tab Navigation */}
            <div className="mb-6 sm:mb-8">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 sm:p-1">
                  <nav className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-1" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab('create')}
                    className={`flex items-center justify-center sm:justify-start px-4 py-4 sm:py-3 rounded-lg font-medium text-sm transition-all duration-200 w-full sm:w-auto ${
                      activeTab === 'create'
                        ? 'bg-primary-500 text-white shadow-md transform scale-105 sm:scale-100'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:shadow-sm'
                    }`}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Post
                  </button>
                  <button
                    onClick={() => setActiveTab('list')}
                    className={`flex items-center justify-center sm:justify-start px-4 py-4 sm:py-3 rounded-lg font-medium text-sm transition-all duration-200 w-full sm:w-auto ${
                      activeTab === 'list'
                        ? 'bg-primary-500 text-white shadow-md transform scale-105 sm:scale-100'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:shadow-sm'
                    }`}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    View Posts
                  </button>
                  <button
                    onClick={() => setActiveTab('popup')}
                    className={`flex items-center justify-center sm:justify-start px-4 py-4 sm:py-3 rounded-lg font-medium text-sm transition-all duration-200 w-full sm:w-auto ${
                      activeTab === 'popup'
                        ? 'bg-primary-500 text-white shadow-md transform scale-105 sm:scale-100'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:shadow-sm'
                    }`}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4 19h6v-6H4v6zM4 13h6V7H4v6zM4 5h6V1H4v4z" />
                    </svg>
                    Popup Manager
                  </button>
                  <a 
                    href="https://simplesmart.com.au" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center sm:justify-start px-4 py-4 sm:py-3 rounded-lg font-medium text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:shadow-sm transition-all duration-200 w-full sm:w-auto"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Documentation
                  </a>
                  </nav>
                </div>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'create' && <PostForm isAuthenticated={isAuthenticated} onAuthRequired={() => setShowAuthModal(true)} />}
            {activeTab === 'list' && (
              <PostList 
                onEditPost={handleEditPost}
                isAuthenticated={isAuthenticated}
                onAuthRequired={() => setShowAuthModal(true)}
                refreshTrigger={refreshTrigger}
              />
            )}
            {activeTab === 'popup' && <PopupManager isAuthenticated={isAuthenticated} onAuthRequired={() => setShowAuthModal(true)} />}

            {/* Edit Modal */}
            {editModalOpen && (
              <PostEditModal 
                post={selectedPost}
                onClose={handleCloseModal}
                onSuccess={handleEditSuccess}
              />
            )}
          </>
        ) : (
          /* Show login prompt when not authenticated */
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                <svg className="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Authentication Required</h3>
              <p className="text-gray-600 mb-6">Please log in to access the CMS features.</p>
              <button
                onClick={() => setShowAuthModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                Login
              </button>
            </div>
          </div>
        )}

        {/* Auth Modal */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onAuthSuccess={handleAuthSuccess}
        />

        {/* Auto-logout Warning Modal */}
        {showLogoutWarning && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
                  <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Session Timeout Warning
                </h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500 mb-4">
                    You will be automatically logged out in:
                  </p>
                  <div className="text-2xl font-bold text-red-600 mb-4">
                    {Math.floor(logoutCountdown / 60)}:{(logoutCountdown % 60).toString().padStart(2, '0')}
                  </div>
                  <p className="text-sm text-gray-500 mb-6">
                    Click "Extend Session" to continue working, or "Logout Now" to end your session.
                  </p>
                </div>
                <div className="flex space-x-3 justify-center">
                  <button
                    onClick={extendSession}
                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    Extend Session
                  </button>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Logout Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
}

export default App;
